// v3.15-1088 — Universal TemplateSelector component.
//
// Pop-over picker shared by every email modal in the system. Sections, in
// the spec-required order:
//   1. Recently Used  (last 5 templates the current user picked)
//   2. Favorites      (per-user pinned templates)
//   3. Categories     (Client Communication / Funding / Risk / Internal / Custom)
//
// Search input at the top filters all three sections simultaneously.
//
// Behavior:
//   - Click template -> calls onSelect({ template, rendered_subject,
//     rendered_body }) so the parent can pump subject + body into its
//     editor state.
//   - Star icon on each row toggles favorite (per-user, fires
//     POST /api/email/templates/{id}/favorite).
//   - On selection, fires POST /api/email/templates/{id}/use so the
//     Recently Used section stays warm.
//
// Designed to be embedded INSIDE another modal (does not itself render a
// Dialog). The trigger is a Button the host renders.
//
// Stabilization-safe: pure UI + read-side template lookups; no send paths.

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Star, Clock, FolderOpen, Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const CATEGORY_LABELS = {
  client_communication: 'Client Communication',
  funding:              'Funding',
  risk:                 'Risk',
  internal:             'Internal',
  custom:               'Custom',
};

// v3.15-1088 — Module-level row component so React doesn't see a new
// component type on every parent render (react/no-unstable-nested-components).
const TemplateRow = ({
  tpl,
  testId,
  isFav,
  isBusy,
  onPick,
  onToggleFav,
  showCategory = false,
}) => (
  <button
    type="button"
    onClick={() => onPick(tpl)}
    disabled={isBusy}
    className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 text-left text-sm group"
    data-testid={`${testId}-row-${tpl.key}`}
  >
    <span
      role="button"
      tabIndex={0}
      onClick={(e) => onToggleFav(tpl.id, e)}
      className={`h-5 w-5 flex items-center justify-center rounded hover:bg-amber-100 ${isFav ? 'text-amber-500' : 'text-slate-300 group-hover:text-amber-400'}`}
      data-testid={`${testId}-fav-${tpl.key}`}
      aria-label={isFav ? 'Remove favorite' : 'Add favorite'}
    >
      <Star className="h-3.5 w-3.5" fill={isFav ? 'currentColor' : 'none'} />
    </span>
    <span className="flex-1 min-w-0">
      <span className="block font-medium text-slate-800 truncate">{tpl.name}</span>
      {showCategory && (
        <span className="block text-[10px] uppercase tracking-wide text-slate-400">
          {CATEGORY_LABELS[tpl.category] || tpl.category}
        </span>
      )}
    </span>
    {isBusy && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
  </button>
);

export const TemplateSelector = ({
  clientId,                       // string | null — drives variable resolution
  onSelect,                       // ({ template, rendered_subject, rendered_body }) => void
  triggerLabel = 'Choose template…',
  testId = 'template-selector',
  triggerVariant = 'outline',
  triggerSize = 'sm',
}) => {
  const [open, setOpen]           = useState(false);
  const [filter, setFilter]       = useState('');
  const [loading, setLoading]     = useState(false);
  const [picking, setPicking]     = useState(null);  // template_id being loaded
  const [categories, setCategories] = useState([]);  // [{ category, templates: [...] }]
  const [recent, setRecent]       = useState([]);
  const [favorites, setFavorites] = useState(new Set());

  // ---------------- data fetch (once on first open, refresh on reopen) ----
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tplRes, recRes] = await Promise.all([
        api.get('/email/templates/v2'),
        api.get('/email/templates/recent', { params: { limit: 5 } }),
      ]);
      setCategories(tplRes?.data?.categories || []);
      setRecent(recRes?.data?.recent || []);
      const favIds = new Set();
      for (const grp of tplRes?.data?.categories || []) {
        for (const t of grp.templates) if (t.is_favorite) favIds.add(t.id);
      }
      setFavorites(favIds);
    } catch (err) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) fetchAll(); }, [open, fetchAll]);

  // ---------------- search filter ----------------------------------------
  const matches = useCallback((t) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return (
      (t.name || '').toLowerCase().includes(q)
      || (t.key || '').toLowerCase().includes(q)
      || (t.description || '').toLowerCase().includes(q)
      || (t.category || '').toLowerCase().includes(q)
    );
  }, [filter]);

  // ---------------- derived data ------------------------------------------
  const filteredRecent = useMemo(() => recent.filter(matches), [recent, matches]);
  const flatTemplates = useMemo(
    () => categories.flatMap((g) => g.templates),
    [categories],
  );
  const favoriteTemplates = useMemo(
    () => flatTemplates.filter((t) => favorites.has(t.id) && matches(t)),
    [flatTemplates, favorites, matches],
  );
  const filteredCategories = useMemo(
    () => categories
      .map((g) => ({ ...g, templates: g.templates.filter(matches) }))
      .filter((g) => g.templates.length > 0),
    [categories, matches],
  );

  // ---------------- actions ----------------------------------------------
  const toggleFavorite = async (tplId, e) => {
    if (e) e.stopPropagation();
    // Optimistic update
    const wasFav = favorites.has(tplId);
    const nextFavs = new Set(favorites);
    if (wasFav) nextFavs.delete(tplId); else nextFavs.add(tplId);
    setFavorites(nextFavs);
    try {
      await api.post(`/email/templates/${tplId}/favorite`);
    } catch {
      // revert on failure
      setFavorites(favorites);
      toast.error('Could not update favorite');
    }
  };

  const pickTemplate = async (tpl) => {
    if (picking) return;
    setPicking(tpl.id);
    try {
      // Render against the client context
      const previewBody = { template_id: tpl.id, ...(clientId ? { client_id: clientId } : {}) };
      const { data: preview } = await api.post('/email/templates/preview', previewBody);
      // Record usage (fire-and-forget — doesn't block selection)
      api.post(`/email/templates/${tpl.id}/use`).catch(() => {});
      onSelect?.({
        template: preview.template,
        rendered_subject: preview.rendered_subject || '',
        rendered_body:    preview.rendered_body || '',
        variables_resolved: preview.variables_resolved || [],
        variables_unknown:  preview.variables_unknown  || [],
      });
      setOpen(false);
      setFilter('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load template');
    } finally {
      setPicking(null);
    }
  };

  // ---------------- row renderer -----------------------------------------
  // (TemplateRow lives at module scope — see top of file.)

  const totalVisible =
    filteredRecent.length + favoriteTemplates.length
    + filteredCategories.reduce((s, g) => s + g.templates.length, 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant={triggerVariant}
          size={triggerSize}
          className="gap-1.5"
          data-testid={`${testId}-trigger`}
        >
          <FileText className="h-3.5 w-3.5" />
          {triggerLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[360px] max-h-[440px] overflow-hidden"
        align="start"
        data-testid={`${testId}-popover`}
      >
        <div className="p-2 border-b border-slate-100">
          <Input
            autoFocus
            placeholder="Search templates…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-8 text-xs"
            data-testid={`${testId}-search`}
          />
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {loading && (
            <div className="px-3 py-6 text-center text-xs text-slate-400 flex items-center justify-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading templates…
            </div>
          )}
          {!loading && totalVisible === 0 && (
            <div className="px-3 py-6 text-center text-xs text-slate-400">
              No templates match.
            </div>
          )}

          {/* --- RECENT --- */}
          {!loading && filteredRecent.length > 0 && (
            <div data-testid={`${testId}-section-recent`}>
              <div className="flex items-center gap-1.5 px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                <Clock className="h-3 w-3" /> Recently Used
              </div>
              {filteredRecent.map((t) => (
                <TemplateRow
                  key={`r-${t.id}`}
                  tpl={t}
                  testId={testId}
                  isFav={favorites.has(t.id)}
                  isBusy={picking === t.id}
                  onPick={pickTemplate}
                  onToggleFav={toggleFavorite}
                  showCategory
                />
              ))}
              <div className="border-b border-slate-100 my-1" />
            </div>
          )}

          {/* --- FAVORITES --- */}
          {!loading && favoriteTemplates.length > 0 && (
            <div data-testid={`${testId}-section-favorites`}>
              <div className="flex items-center gap-1.5 px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                <Star className="h-3 w-3" /> Favorites
              </div>
              {favoriteTemplates.map((t) => (
                <TemplateRow
                  key={`f-${t.id}`}
                  tpl={t}
                  testId={testId}
                  isFav={favorites.has(t.id)}
                  isBusy={picking === t.id}
                  onPick={pickTemplate}
                  onToggleFav={toggleFavorite}
                  showCategory
                />
              ))}
              <div className="border-b border-slate-100 my-1" />
            </div>
          )}

          {/* --- CATEGORIES --- */}
          {!loading && filteredCategories.map((g) => (
            <div key={`g-${g.category}`} data-testid={`${testId}-section-${g.category}`}>
              <div className="flex items-center gap-1.5 px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                <FolderOpen className="h-3 w-3" />
                {CATEGORY_LABELS[g.category] || g.category}
              </div>
              {g.templates.map((t) => (
                <TemplateRow
                  key={`c-${t.id}`}
                  tpl={t}
                  testId={testId}
                  isFav={favorites.has(t.id)}
                  isBusy={picking === t.id}
                  onPick={pickTemplate}
                  onToggleFav={toggleFavorite}
                />
              ))}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default TemplateSelector;
